import { Button, MenuItem, Select } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePicker, Select as AntSelect } from "antd";
import dayjs from "dayjs";
import moment from "moment";

import HeaderBread from "../../../../components/HeaderBread";
import { useDataLayerValue } from "../../../../DataLayer/DataLayer";
import Layout from "../../../../Layout";
import CustomHeaderTitle from "../../../../components/constants/CustomHeaderTitle";
import CustomizedTable from "../../../../components/UI/CustomizedTable";
import SearchField from "../../../../components/SearchField";
import { getShorexBookings, getShorexBookingPackageOptions } from "../../../../utils/ApiCalls/BackOffice/Shorex";
import { useDebounce } from "../../../../hooks";

const { RangePicker } = DatePicker;

const getDefaultCreatedAt = () =>
  JSON.stringify([
    moment().startOf("month").format("YYYY-MM-DD"),
    moment().format("YYYY-MM-DD"),
  ]);

const parseCreatedAtRange = (createdAt) => {
  if (!createdAt) return null;
  try {
    const [start, end] = JSON.parse(createdAt);
    if (start && end) return [dayjs(start, "YYYY-MM-DD"), dayjs(end, "YYYY-MM-DD")];
  } catch (e) {
    // unparsable — treat as no selection
  }
  return null;
};

const statusChipStyle = (status) => {
  const base = {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#fff",
  };
  switch ((status || "").toLowerCase()) {
    case "booked":
      return { ...base, background: "#0ab402" };
    case "failed":
      return { ...base, background: "#eb6363" };
    case "pending":
      return { ...base, background: "#f5a623" };
    default:
      return { ...base, background: "#9e9e9e" };
  }
};

const ShoreExcursionsReport = () => {
  const navigate = useNavigate();
  const locationP = useLocation();
  const [, dispatch] = useDataLayerValue();

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Report", route: "/shore/back-office/shore-excursions/report" },
  ];

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [createdAt, setCreatedAt] = useState(() => {
    const params = new URLSearchParams(locationP.search);
    return params.has("createdAt") ? params.get("createdAt") : getDefaultCreatedAt();
  });
  const [status, setStatus] = useState(
    () => new URLSearchParams(locationP.search).get("status") || ""
  );
  const [page, setPage] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("page")) || 1
  );
  const [pageSize, setPageSize] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("pageSize")) || 10
  );
  const [search, setSearch] = useState(
    () => new URLSearchParams(locationP.search).get("search") || ""
  );
  const [excDate, setExcDate] = useState(
    () => new URLSearchParams(locationP.search).get("excDate") || ""
  );
  const [excIds, setExcIds] = useState(() => {
    const val = new URLSearchParams(locationP.search).get("excIds");
    return val ? val.split(",").map(Number).filter(Boolean) : [];
  });
  const [packageOptions, setPackageOptions] = useState([]);
  const [sorts, setSorts] = useState([]);
  const debouncedSearch = useDebounce(search, 1000);

  useEffect(() => {
    getShorexBookingPackageOptions().then((res) =>
      setPackageOptions(
        (res.data || []).map((p) => ({
          value: p.excId,
          label: p.excNo ? `${p.name} (${p.excNo})` : p.name,
        }))
      )
    );
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(locationP.search);
    setSearch(params.get("search") || "");
  }, [locationP.search]);

  useEffect(() => {
    const params = new URLSearchParams();
    const currentParams = new URLSearchParams(locationP.search);
    params.set("page", page);
    params.set("pageSize", pageSize);
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    if (excDate) params.set("excDate", excDate);
    if (excIds.length) params.set("excIds", excIds.join(","));
    params.set("createdAt", createdAt);
    if (currentParams.toString() !== params.toString()) {
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [page, pageSize, debouncedSearch, status, createdAt, excDate, excIds, navigate, locationP.search]);

  const columns = [
    {
      title: "Sl",
      dataIndex: "id",
      key: "id",
      width: 40,
      render: (val, rec, idx) => (page - 1) * pageSize + idx + 1,
    },
    {
      title: "Transaction ID",
      dataIndex: "uniqId",
      key: "transactionId",
      width: 220,
    },
    {
      title: "Acc ID",
      dataIndex: "accId",
      key: "accId",
      width: 100,
    },
    {
      title: "Shorex Code",
      dataIndex: "excNo",
      key: "excNo",
      width: 130,
      sorter: true,
      render: (val) => val || "-",
    },
    {
      title: "Excursion Date",
      dataIndex: "excDate",
      key: "excDate",
      width: 150,
      sorter: true,
      // excDate is a calendar date, not an instant - .utc() reads its date
      // part directly instead of letting the browser's local tz shift it.
      render: (val) => (val ? moment.utc(val).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Package Name",
      dataIndex: "packageName",
      key: "packageName",
      width: 180,
      render: (val) => val || "-",
    },
    {
      title: "Cabin Number(s)",
      dataIndex: "cabinNumbers",
      key: "cabinNumbers",
      width: 150,
      render: (val) => (Array.isArray(val) && val.length ? val.join(", ") : "-"),
    },
    {
      title: "Adult",
      dataIndex: "adultTicket",
      key: "adultTicket",
      width: 70,
    },
    {
      title: "Child",
      dataIndex: "childTicket",
      key: "childTicket",
      width: 70,
    },
    {
      title: "Infant",
      dataIndex: "infantTicket",
      key: "infantTicket",
      width: 70,
    },
    {
      title: "Total Price",
      dataIndex: "totalPrice",
      key: "totalPrice",
      width: 120,
      render: (val, rec) =>
        val != null ? `${rec.currency || ""} ${Number(val).toFixed(2)}` : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (val) => <span style={statusChipStyle(val)}>{val || "-"}</span>,
    },
    {
      title: "Booking Date & Time",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      sorter: true,
      render: (val) => (val ? moment(val).format("DD-MM-YYYY HH:mm") : "-"),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (text, record) => (
        <div className="table_action_btn">
          <button
            className="btn delete_btn"
            title="View"
            onClick={() =>
              navigate("/shore/back-office/shore-excursions/report/view/" + record.id, {
                state: { from: locationP.pathname + locationP.search },
              })
            }
          >
            <i className="ri-eye-line"></i>
          </button>
        </div>
      ),
    },
  ];

  const getData = async (value) => {
    dispatch({ type: "SET_LOADING", loading: true });
    const args = {
      page: value || page,
      limit: pageSize,
      createdAt,
    };
    if (debouncedSearch) args.search = debouncedSearch;
    if (status) args.status = status;
    if (excDate) args.excDate = excDate;
    if (excIds.length) args.excIds = excIds.join(",");
    if (sorts.length) {
      args.sort = sorts.map((x) => `${x.columnKey}:${x.order.toUpperCase()}`).join(",");
    }
    let data = await getShorexBookings(args);
    if (data.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
    } else {
      setRows(data.data || []);
      setCount(data.count || 0);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  useEffect(() => {
    getData(page);
  }, [page, pageSize, debouncedSearch, status, createdAt, excDate, excIds, sorts]);

  return (
    <Layout type={"read_per"} module_data={"Report"}>
      <CustomHeaderTitle title={"Shore Excursions Report"} />
      <div>
        <HeaderBread navigation={navigation} title={"Report"} />
      </div>
      <section className="content" style={{ marginTop: "20px" }}>
        <div className="container-fluid">
          <div className="card">
            <div className="card-header two_item" style={{ alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <Select
                  style={{ width: "160px", height: "40px" }}
                  displayEmpty
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setPage(1);
                  }}
                  renderValue={status !== "" ? undefined : () => "All Status"}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="booked">Booked</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
                <RangePicker
                  style={{ width: "250px" }}
                  onChange={(date, dateString) => {
                    setCreatedAt(
                      dateString[0] && dateString[1] ? JSON.stringify(dateString) : ""
                    );
                    setPage(1);
                  }}
                  value={parseCreatedAtRange(createdAt)}
                  format={"YYYY-MM-DD"}
                  placeholder={["Booked from", "Booked to"]}
                  disabledDate={(current) => moment() <= current}
                />
                <RangePicker
                  style={{ width: "250px" }}
                  onChange={(date, dateString) => {
                    setExcDate(dateString[0] && dateString[1] ? JSON.stringify(dateString) : "");
                    setPage(1);
                  }}
                  value={parseCreatedAtRange(excDate)}
                  format={"YYYY-MM-DD"}
                  placeholder={["Excursion from", "Excursion to"]}
                />
                <AntSelect
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  maxTagCount="responsive"
                  style={{ width: "280px" }}
                  placeholder="Package name"
                  options={packageOptions}
                  value={excIds}
                  onChange={(vals) => {
                    setExcIds(vals);
                    setPage(1);
                  }}
                />
                <Button
                  variant="contained"
                  style={{ background: "var(--red)", fontWeight: "500" }}
                  onClick={() => {
                    setStatus("");
                    setCreatedAt(getDefaultCreatedAt());
                    setExcDate("");
                    setExcIds([]);
                    setSearch("");
                    setPage(1);
                  }}
                >
                  Reset
                </Button>
              </div>
              <SearchField
                value={search}
                SearchValue={() => setPage(1)}
                setValue={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="card-body">
              <div className="table_holder">
                <CustomizedTable
                  setRows={setRows}
                  setPageSize={setPageSize}
                  setPage={setPage}
                  pageSize={pageSize}
                  page={page}
                  count={count}
                  columns={columns}
                  sorts={sorts}
                  sortHandler={({ columnKey }) => {
                    setSorts((prev) => {
                      const current = prev.find((x) => x.columnKey === columnKey);
                      if (!current) return [...prev, { columnKey, order: "asc" }];
                      if (current.order === "asc") {
                        return prev.map((x) => (x.columnKey === columnKey ? { ...x, order: "desc" } : x));
                      }
                      return prev.filter((x) => x.columnKey !== columnKey);
                    });
                    setPage(1);
                  }}
                  rows={rows}
                  title={"Shore Excursions Report"}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ShoreExcursionsReport;
